/**
 * Content Sanitization Utilities for XSS Protection
 *
 * This module provides comprehensive sanitization functions to prevent
 * Cross-Site Scripting (XSS) attacks by cleaning user input and HTML content.
 */

import DOMPurify from "dompurify";

/**
 * Configuration for different sanitization levels
 */
export interface SanitizationConfig {
  allowedTags?: string[];
  allowedAttributes?: string[];
  stripTags?: boolean;
  allowLinks?: boolean;
  allowImages?: boolean;
}

/**
 * Predefined sanitization configurations
 */
export const SANITIZATION_CONFIGS = {
  // Strict: Only allow basic text formatting
  STRICT: {
    allowedTags: ["b", "i", "em", "strong", "u"],
    allowedAttributes: [],
    stripTags: false,
    allowLinks: false,
    allowImages: false,
  },

  // Basic: Allow common formatting and lists
  BASIC: {
    allowedTags: ["b", "i", "em", "strong", "u", "p", "br", "ul", "ol", "li"],
    allowedAttributes: [],
    stripTags: false,
    allowLinks: false,
    allowImages: false,
  },

  // Rich: Allow most formatting including links (for lesson plans)
  RICH: {
    allowedTags: [
      "b",
      "i",
      "em",
      "strong",
      "u",
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "a",
    ],
    allowedAttributes: ["href", "title"],
    stripTags: false,
    allowLinks: true,
    allowImages: false,
  },

  // Plain text only
  TEXT_ONLY: {
    allowedTags: [],
    allowedAttributes: [],
    stripTags: true,
    allowLinks: false,
    allowImages: false,
  },
} as const;

/**
 * Sanitize HTML content using DOMPurify
 */
export const sanitizeHtml = (
  content: string,
  config: SanitizationConfig = SANITIZATION_CONFIGS.BASIC
): string => {
  if (!content || typeof content !== "string") {
    return "";
  }

  // Configure DOMPurify based on our config
  const purifyConfig: DOMPurify.Config = {
    ALLOWED_TAGS: config.allowedTags || [],
    ALLOWED_ATTR: config.allowedAttributes || [],
    KEEP_CONTENT: !config.stripTags,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SANITIZE_DOM: true,
    SANITIZE_NAMED_PROPS: true,
    FORBID_SCRIPT: true,
    FORBID_TAGS: ["script", "object", "embed", "form", "input", "button"],
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
    ],
  };

  // Additional security for links
  if (!config.allowLinks) {
    purifyConfig.FORBID_TAGS.push("a");
  } else {
    // If links are allowed, ensure they're safe
    purifyConfig.ADD_ATTR = ["target"];
    purifyConfig.ALLOWED_URI_REGEXP =
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;
  }

  // Additional security for images
  if (!config.allowImages) {
    purifyConfig.FORBID_TAGS.push("img");
  }

  try {
    const sanitized = DOMPurify.sanitize(content, purifyConfig);

    // Additional post-processing for links to ensure they open safely
    if (config.allowLinks && sanitized.includes("<a ")) {
      return sanitized.replace(
        /<a /g,
        '<a target="_blank" rel="noopener noreferrer" '
      );
    }

    return sanitized;
  } catch (error) {
    console.error("Sanitization error:", error);
    // Fallback to plain text if sanitization fails
    return sanitizeText(content);
  }
};

/**
 * Sanitize plain text input (removes all HTML)
 */
export const sanitizeText = (input: string): string => {
  if (!input || typeof input !== "string") {
    return "";
  }

  // Remove all HTML tags and decode HTML entities
  const withoutTags = input.replace(/<[^>]*>/g, "");

  // Decode common HTML entities
  const decoded = withoutTags
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, "/")
    .replace(/&#x60;/g, "`");

  // Trim whitespace and normalize line breaks
  return decoded.trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
};

/**
 * Sanitize form input data
 */
export const sanitizeFormInput = (input: string): string => {
  return sanitizeText(input);
};

/**
 * Sanitize lesson plan content (allows rich formatting)
 */
export const sanitizeLessonPlanContent = (content: string): string => {
  return sanitizeHtml(content, SANITIZATION_CONFIGS.RICH);
};

/**
 * Sanitize assessment content (allows basic formatting)
 */
export const sanitizeAssessmentContent = (content: string): string => {
  return sanitizeHtml(content, SANITIZATION_CONFIGS.BASIC);
};

/**
 * Sanitize user profile data (text only)
 */
export const sanitizeUserProfile = (input: string): string => {
  return sanitizeText(input);
};

/**
 * Sanitize search queries (text only)
 */
export const sanitizeSearchQuery = (query: string): string => {
  const sanitized = sanitizeText(query);

  // Additional validation for search queries
  if (sanitized.length > 200) {
    return sanitized.substring(0, 200);
  }

  return sanitized;
};

/**
 * Validate and sanitize URL inputs
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== "string") {
    return "";
  }

  // Remove any HTML tags first
  const cleanUrl = sanitizeText(url);

  // Basic URL validation
  try {
    const urlObj = new URL(cleanUrl);

    // Only allow http, https, and mailto protocols
    if (!["http:", "https:", "mailto:"].includes(urlObj.protocol)) {
      return "";
    }

    return urlObj.toString();
  } catch {
    // If URL is invalid, return empty string
    return "";
  }
};

/**
 * Sanitize file names for uploads
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName || typeof fileName !== "string") {
    return "";
  }

  // Remove path traversal attempts and dangerous characters
  const sanitized = fileName
    .replace(/[<>:"/\\|?*]/g, "") // Remove dangerous characters
    .replace(/\.\./g, "") // Remove path traversal
    .replace(/^\.+/, "") // Remove leading dots
    .trim();

  // Ensure filename isn't empty and has reasonable length
  if (!sanitized || sanitized.length === 0) {
    return "untitled";
  }

  if (sanitized.length > 255) {
    return sanitized.substring(0, 255);
  }

  return sanitized;
};

/**
 * Comprehensive input sanitization for API requests
 */
export const sanitizeApiInput = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string") {
      // Sanitize string values
      sanitized[key] = sanitizeFormInput(value);
    } else if (typeof value === "number" || typeof value === "boolean") {
      // Keep numbers and booleans as-is
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      // Recursively sanitize arrays
      sanitized[key] = value.map(item =>
        typeof item === "string" ? sanitizeFormInput(item) : item
      );
    } else if (value && typeof value === "object") {
      // Recursively sanitize objects
      sanitized[key] = sanitizeApiInput(value);
    } else {
      // For other types, convert to string and sanitize
      sanitized[key] = sanitizeFormInput(String(value || ""));
    }
  }

  return sanitized;
};

/**
 * Security validation for content before display
 */
export const validateContentSecurity = (
  content: string
): {
  isSecure: boolean;
  issues: string[];
  sanitized: string;
} => {
  const issues: string[] = [];

  // Check for potential XSS patterns
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /data:text\/html/i,
  ];

  for (const pattern of xssPatterns) {
    if (pattern.test(content)) {
      issues.push(`Potential XSS pattern detected: ${pattern.source}`);
    }
  }

  const sanitized = sanitizeHtml(content, SANITIZATION_CONFIGS.BASIC);
  const isSecure = issues.length === 0;

  return {
    isSecure,
    issues,
    sanitized,
  };
};
